// Capacitor runtime bootstrap. Called once from main.jsx on app start.
// All logic is behind Capacitor.isNativePlatform(), so this module is a
// cheap no-op when the app runs in a plain browser.
//
// What it handles:
//   - Hides the native splash screen once React has mounted
//   - Sets the status bar style to match the dark app chrome
//   - Routes the Android hardware back button through the SPA's history
//     so it doesn't instantly kill the app on the first tap
//   - Nudges the keyboard-driven layout mode so inputs stay in view

import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";
import { App } from "@capacitor/app";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";

export function initCapacitor() {
  if (!Capacitor.isNativePlatform()) return;

  // Status bar — dark theme app, so icons should render light.
  // overlaysWebView=false reserves the status bar strip for the OS and
  // keeps our safe-area padding from double-counting.
  StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
  StatusBar.setBackgroundColor({ color: "#11141B" }).catch(() => {});
  StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});

  // Dismiss the splash screen once React has rendered the first frame.
  // The splash is configured by the native projects' resources; here we
  // just tell it we're ready. 300ms gives React time to paint content.
  setTimeout(() => {
    SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {});
  }, 300);

  // Android back button → window.history.back() when there's history,
  // otherwise minimize instead of hard-exit. Without this, the hardware
  // back button closes the app on first press, which is a known
  // first-impression killer on Google Play reviews.
  App.addListener("backButton", ({ canGoBack }) => {
    if (canGoBack && window.history.length > 1) {
      window.history.back();
    } else {
      App.minimizeApp().catch(() => {});
    }
  }).catch(() => {});

  // Keyboard resize mode: shrink the viewport when the keyboard opens
  // so focused inputs don't get hidden behind it. "native" is the right
  // mode for form-heavy content.
  Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => {});

  // Push notifications — register for FCM, request permission, hand
  // the device token to the backend. Lazy-imported so web stays cheap.
  import("./push").then(({ initPush }) => initPush()).catch(err => {
    console.warn("[capacitor] push init failed:", err.message);
  });

  // Deep links — listen for incoming gabruz:// URLs from external
  // sources (emails, chats, other apps). Push-tap deep linking lives
  // in src/push.js and reuses the same dispatcher.
  import("./deepLinks").then(({ initDeepLinks }) => initDeepLinks()).catch(err => {
    console.warn("[capacitor] deep links init failed:", err.message);
  });
}
